import { prop, reduce, sortBy } from 'ramda'
import Observable from 'app/common/classes/observable'

export const stuckAttributeName = 'stuck'
export const shadowAttributeName = 'shadow'

type StickyItem = {
  height: number
  applyToChildrenTh: boolean
  resetShift: boolean
  skipShift: boolean
  stuck: boolean
  node: HTMLElement | null
}

const emptyStickyItem: StickyItem = {
  height: 0,
  applyToChildrenTh: false,
  resetShift: false,
  skipShift: false,
  stuck: false,
  node: null,
}

type AddOptions = {
  applyToChildrenTh?: boolean
  resetShift?: boolean
  skipShift?: boolean
}

const defaultAddOptions: AddOptions = {
  applyToChildrenTh: false,
  resetShift: false,
  skipShift: false,
}

const createStyle = (ID: number, shift: number = 0) => {
  if (ID > 0) {
    return `position: sticky; top: ${shift}px; z-index: 1;`
  }
  return `position: sticky; bottom: ${shift}px; z-index: 1;`
}

const assignStyle = (node: Element, style: string, applyToChildrenTh: boolean) => {
  const stylingNodes = applyToChildrenTh ? node.querySelectorAll('th') : [node]
  stylingNodes.forEach((nodeItem) => nodeItem.setAttribute('style', style))
}

/**
 * Sticky class
 */

class Sticky extends Observable {
  IDByNode: Map<Element, number> = new Map()

  stickyItemByID: Map<number, StickyItem> = new Map()

  ro: ResizeObserver

  ioByID: Map<number, IntersectionObserver> = new Map()

  constructor() {
    super()
    this.ro = new ResizeObserver(this.onResize)
  }

  calcShift(targetID: number): number {
    const { resetShift: rsh } = this.stickyItemByID.get(targetID) || emptyStickyItem
    if (rsh) return 0
    const items = Array.from(this.stickyItemByID.entries())
    if (targetID < 0) {
      const actualItems = items.filter(([ID]) => ID < targetID)
      // для прилипаний к низу resetShift, skipShift не реализовано
      const shift = reduce((acc, [, { height }]) => acc + height, 0, actualItems)
      return shift
    }
    const actualItems = items.filter(([ID]) => ID > 0 && ID < targetID)
    const actualSortedItems = sortBy(prop(0), actualItems)
    const shift = reduce(
      (acc, [, { height, resetShift, skipShift }]) => {
        const actualHeight = skipShift ? 0 : height
        return resetShift ? actualHeight : acc + actualHeight
      },
      0,
      actualSortedItems,
    )

    return shift
  }

  updateStyles = (el: HTMLElement, ID: number) => {
    const current = this.stickyItemByID.get(ID) || emptyStickyItem
    const shift = this.calcShift(ID)
    assignStyle(el, createStyle(ID, shift), current.applyToChildrenTh)
  }

  onResize = (entries: Array<ResizeObserverEntry>) => {
    entries.forEach(({ target }: ResizeObserverEntry) => {
      const { height = 0 } = target.getBoundingClientRect()
      // другой вариант получения высоты (contentRect из ResizeObserverEntry),
      // но работает некорректно
      // const { height: heightClear = 0 } = contentRect
      // const targetStyle = window.getComputedStyle(target)
      // const padding = (parseInt(targetStyle.getPropertyValue('padding-top'), 10)
      //   + parseInt(targetStyle.getPropertyValue('padding-bottom'), 10))
      // const height = heightClear + padding + 1

      const ID = this.IDByNode.get(target) || 0

      if (!ID) {
        console.warn('👻 not found ID by node (sticky, onResize)')
        return
      }
      const current = this.stickyItemByID.get(ID) || emptyStickyItem
      this.stickyItemByID.set(ID, { ...current, height })

      // обновление смещения в стилях
      const shift = this.calcShift(ID)
      assignStyle(target, createStyle(ID, shift), current.applyToChildrenTh)

      // обновление смещения всех элементов в детекторе пересечений
      const firstID = reduce(
        (acc, val) => Math.min(acc, val),
        0,
        Array.from(this.stickyItemByID.keys()),
      )
      const node = this.stickyItemByID.get(firstID)?.node
      if (node) {
        this.addIntersectionObserver(firstID, node)
      }
    })
  }

  onIntersect = (entries: Array<IntersectionObserverEntry>) => {
    const entrie = entries[0]
    const { target, intersectionRatio } = entrie
    const ID = this.IDByNode.get(target) || 0

    if (!ID) {
      console.warn('👻 not found ID by node (sticky, onIntersect)')
      return
    }
    const stuckStatus = intersectionRatio < 1
    const current = this.stickyItemByID.get(ID) || emptyStickyItem
    this.stickyItemByID.set(ID, { ...current, stuck: stuckStatus })

    const items = Array.from(this.stickyItemByID.entries())
    const sorted = sortBy(prop(0), items)

    sorted.forEach(([id, { node, stuck }], index, allItems) => {
      if (node) {
        if (id < 0) {
          // прилипание снизу
          node.toggleAttribute(stuckAttributeName, stuck)
          const nextItemStucked = allItems[index + 1] && allItems[index + 1][1].stuck
          const isTopItem = !allItems.find(([x]) => x < 0 && x > id)
          const shadow = stuck && (!nextItemStucked || isTopItem)
          node.toggleAttribute(shadowAttributeName, shadow)
          return
        }
        node.toggleAttribute(stuckAttributeName, stuck)
        const nextItemStucked = allItems[index + 1] && allItems[index + 1][1].stuck
        const shadow = stuck && !nextItemStucked
        node.toggleAttribute(shadowAttributeName, shadow)
      }
    })

    this.updateSubscribers()
  }

  addResizeObserver(el: HTMLElement) {
    this.ro.observe(el)
  }

  addIntersectionObserver(ID: number, el: HTMLElement) {
    const shift = this.calcShift(ID) // для прилипаний снизу вернет 0 для первого элемента,
    // что не является реальным смещением, если ниже есть еще элементы, поэтому...(см. след. коммент)

    const correction = ID > 2 ? 2 : 1
    const intersectionMargin =
      ID > 0 ? `${-shift - correction}px 0px 0px 0px` : `0px 0px ${-shift - 1}px 0px`

    const currentIntersectionObserver = this.ioByID.get(ID)
    if (currentIntersectionObserver) {
      currentIntersectionObserver.disconnect()
    }

    const io = new IntersectionObserver(this.onIntersect, {
      threshold: 1,
      rootMargin: intersectionMargin,
    })
    io.observe(el)

    this.ioByID.set(ID, io)
    // ...поэтому перескан всех предыдущих элементов рекурсией (только для прилипания снизу),
    // чтоб правильно расчитать смещение для элементов находящихся выше в доме
    if (ID < -1) {
      const prevID = ID + 1
      const prevNode = this.stickyItemByID.get(prevID)?.node || null
      if (prevNode) {
        this.addIntersectionObserver(prevID, prevNode)
      }
    }
  }

  add(el: HTMLElement, ID: number, addOptions?: AddOptions) {
    const {
      applyToChildrenTh = false,
      resetShift = false,
      skipShift = false,
    } = { ...defaultAddOptions, ...addOptions }

    assignStyle(el, createStyle(ID), applyToChildrenTh)

    this.IDByNode.set(el, ID)
    const { height = 0 } = el.getBoundingClientRect()
    this.stickyItemByID.set(ID, {
      ...emptyStickyItem,
      height,
      applyToChildrenTh,
      resetShift,
      skipShift,
      node: el,
    })

    this.addResizeObserver(el)
    this.addIntersectionObserver(ID, el)
  }

  remove(el: HTMLElement) {
    this.ro.unobserve(el)
    const ID = this.IDByNode.get(el)
    this.IDByNode.delete(el)
    if (ID) {
      this.stickyItemByID.delete(ID)
      const io = this.ioByID.get(ID)
      if (io) {
        io.disconnect()
      }
    }
  }
}

export default new Sticky()
